export enum InventoryShortageStatus {
  Open = 1,
  PartiallyCovered = 2,
  Covered = 3,
  Cancelled = 4,
  SettledAsVariance = 5,
}

export enum InventoryShortageCoverageType {
  Batch = 1,
  ReceiptCorrection = 2,
}

/**
 * Why neither resolution button is offered. `null` means no guard applies — the
 * row is actionable — so seeing it alongside two hidden buttons can only mean
 * the operator lacks both permissions.
 */
export enum InventoryShortageUnavailableReason {
  OrderNotCompleted = "order_not_completed",
  AlreadyResolved = "shortage_already_resolved",
}

/**
 * Refusals from `cover` and `receipt-correction`. Branch on the key, never on
 * the translated `errors[]` text.
 */
export enum InventoryShortageErrorKey {
  ResolutionRequiresCompletedOrder = "inventory_shortage_resolution_requires_completed_order",
  CorrectionRequiresCompletedOrder = "inventory_shortage_correction_requires_completed_order",
  AlreadyResolved = "inventory_shortage_already_resolved",
  ExceedsRemaining = "inventory_shortage_cover_exceeds_remaining",
  ExceedsBatchAvailable = "inventory_shortage_cover_exceeds_batch_available",
  /** The product/store pair is checked on both paths, so a correction can raise
   * this too — the restricted check delegates to the general one. */
  BatchNotInStore = "inventory_shortage_batch_not_in_store",
  /** Correction only, and only while the line has allocations — the guard is
   * `allocatedBatchIds->isNotEmpty()`, so it never collides with the one above. */
  BatchNotSource = "inventory_shortage_batch_not_source",
  OperationTokenConflict = "inventory_shortage_operation_token_conflict",
  IntegrityFailed = "inventory_shortage_integrity_failed",
}

/** The backend rejects notes shorter than this on a receipt correction. */
export const INVENTORY_SHORTAGE_NOTES_MIN_LENGTH = 3;
export const INVENTORY_SHORTAGE_NOTES_MAX_LENGTH = 2000;

export const getInventoryShortageStatusLabel = (status?: number | null) => {
  switch (status) {
    case InventoryShortageStatus.Open:
      return "inventory_shortage_status_open";
    case InventoryShortageStatus.PartiallyCovered:
      return "inventory_shortage_status_partially_covered";
    case InventoryShortageStatus.Covered:
      return "inventory_shortage_status_covered";
    case InventoryShortageStatus.Cancelled:
      return "inventory_shortage_status_cancelled";
    case InventoryShortageStatus.SettledAsVariance:
      return "inventory_shortage_status_settled_as_variance";
    default:
      return "not_determined";
  }
};

export const getInventoryShortageStatusColor = (status?: number | null) => {
  switch (status) {
    case InventoryShortageStatus.Open:
      return "error";
    case InventoryShortageStatus.PartiallyCovered:
      return "warning";
    case InventoryShortageStatus.Covered:
      return "success";
    case InventoryShortageStatus.Cancelled:
      return "default";
    case InventoryShortageStatus.SettledAsVariance:
      return "purple";
    default:
      return "default";
  }
};

export const getInventoryShortageCoverageTypeLabel = (type?: number | null) => {
  switch (type) {
    case InventoryShortageCoverageType.Batch:
      return "inventory_shortage_coverage_type_batch";
    case InventoryShortageCoverageType.ReceiptCorrection:
      return "inventory_shortage_coverage_type_receipt_correction";
    default:
      return "not_determined";
  }
};

export const getInventoryShortageUnavailableReasonLabel = (
  reason?: string | null,
) => {
  switch (reason) {
    case InventoryShortageUnavailableReason.OrderNotCompleted:
      return "inventory_shortage_unavailable_order_not_completed";
    case InventoryShortageUnavailableReason.AlreadyResolved:
      return "inventory_shortage_unavailable_already_resolved";
    default:
      // No reason means the row is actionable but this operator may not act.
      return "not_allowed";
  }
};

export const getInventoryShortageErrorLabel = (errorKey?: string | null) => {
  switch (errorKey) {
    case InventoryShortageErrorKey.ResolutionRequiresCompletedOrder:
    case InventoryShortageErrorKey.CorrectionRequiresCompletedOrder:
      return "inventory_shortage_error_requires_completed_order";
    case InventoryShortageErrorKey.AlreadyResolved:
      return "inventory_shortage_error_already_resolved";
    case InventoryShortageErrorKey.ExceedsRemaining:
      return "inventory_shortage_error_exceeds_remaining";
    case InventoryShortageErrorKey.ExceedsBatchAvailable:
      return "inventory_shortage_error_exceeds_batch_available";
    case InventoryShortageErrorKey.BatchNotInStore:
      return "inventory_shortage_error_batch_not_in_store";
    case InventoryShortageErrorKey.BatchNotSource:
      return "inventory_shortage_error_batch_not_source";
    case InventoryShortageErrorKey.OperationTokenConflict:
      return "inventory_shortage_error_operation_token_conflict";
    case InventoryShortageErrorKey.IntegrityFailed:
      return "inventory_shortage_error_integrity_failed";
    default:
      return "operation_failed";
  }
};
